import { ReactNode, useLayoutEffect, useRef, useState } from 'react';
import { Router } from 'react-router-dom';

import { createBrowserHistory, History, To } from '@remix-run/router';

type InterceptingBrowserRouterProps = Readonly<{
  children: ReactNode;
  shouldBlock: (to: To) => boolean;
  onBlocked: (to: To) => void;
}>;

// BrowserRouter whose navigator runs every push/replace through shouldBlock first, so a
// blocked navigation never touches the URL. useNavigate() and <Link> both funnel through here.
// Only push/replace are wrapped; every other navigator read hits the native history untouched.
export const InterceptingBrowserRouter = ({
  children,
  shouldBlock,
  onBlocked,
}: InterceptingBrowserRouterProps) => {
  const shouldBlockRef = useRef(shouldBlock);
  const onBlockedRef = useRef(onBlocked);

  shouldBlockRef.current = shouldBlock;
  onBlockedRef.current = onBlocked;

  const navigatorRef = useRef<History>();

  if (navigatorRef.current == null) {
    const history = createBrowserHistory({ window, v5Compat: true });
    const { push, replace } = history;

    history.push = (to, state) => {
      if (shouldBlockRef.current(to)) {
        onBlockedRef.current(to);

        return;
      }

      push(to, state);
    };

    history.replace = (to, state) => {
      if (shouldBlockRef.current(to)) {
        onBlockedRef.current(to);

        return;
      }

      replace(to, state);
    };

    navigatorRef.current = history;
  }

  const navigator = navigatorRef.current;

  const [state, setState] = useState({ action: navigator.action, location: navigator.location });

  useLayoutEffect(() => navigator.listen(setState), [navigator]);

  return (
    <Router location={state.location} navigationType={state.action} navigator={navigator}>
      {children}
    </Router>
  );
};
